/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import ErrorBoundary from './shared/components/ErrorBoundary';
import { SettingsProvider } from './core/contexts/SettingsContext';
import { ClientsProvider } from './core/contexts/ClientsContext';
import { CategoriesProvider } from './core/contexts/CategoriesContext';
import { ProductsProvider } from './core/contexts/ProductsContext';
import { DocumentsProvider } from './core/contexts/DocumentsContext';
import { DocumentTypesProvider } from './core/contexts/DocumentTypesContext';
import { PaymentsProvider } from './core/contexts/PaymentsContext';
import { EmployeesProvider } from './core/contexts/EmployeesContext';
import { TransportersProvider } from './core/contexts/TransportersContext';
import { ReturnsProvider } from './core/contexts/ReturnsContext';
import { ThemeProvider } from './core/contexts/ThemeContext';
import { LanguageProvider } from './core/contexts/LanguageContext';
// ❌ DashboardThemeProvider DELETED - Using global theme system now
import { AppDataProvider } from './core/contexts/AppDataContext';
import { PermissionsProvider } from './core/contexts/PermissionsContext';
import { TimeTrackingProvider } from './core/contexts/TimeTrackingContext';
import Layout from './shared/components/layout/Layout';
import { useSetup } from './core/contexts/SetupContext';
import authService from './core/services/authService';
import licensingService from './core/services/licensingService';
import { AIAgentPanel, AIAgentProvider } from './ai-agent';
import UpdateNotificationBar from './shared/components/updates/UpdateNotificationBar';
import { dbService } from './core/services';
import FeatureAwareRouter from './shared/components/FeatureAwareRouter';
import { initSecureStorage } from './shared/utils/secureStorage';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'react-hot-toast';

import './index.css';
import { initializeTheme } from './core/utils/globalTheme';

import { isCloudDemoRuntime } from './shared/utils/publicDemoRuntime';
import { isSaudiBrand } from './products/saudi/utils/isSaudiBrand';
import { applySaudiBrandRuntime } from './products/saudi/utils/applySaudiBrandRuntime';
import { isQawaemDirectAccess } from './products/saudi/utils/isQawaemDirectAccess';
import { ensureQawaemDirectAccessSession } from './products/saudi/utils/ensureQawaemDirectAccessSession';

const IS_CLOUD_DEMO = isCloudDemoRuntime();

function LanguageHandler() {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (isSaudiBrand()) {
      applySaudiBrandRuntime();
      return;
    }

    const savedSettings = localStorage.getItem('qawaem-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        if (parsed.invoicing && parsed.invoicing.language) {
          i18n.changeLanguage(parsed.invoicing.language);
          if (parsed.invoicing.language === 'ar') {
            document.documentElement.dir = 'rtl';
            document.documentElement.lang = 'ar';
          } else {
            document.documentElement.dir = 'ltr';
            document.documentElement.lang = parsed.invoicing.language;
          }
        }
      } catch (error) {
        console.error('Error loading language settings:', error);
      }
    }
  }, [i18n]);

  return null;
}

function DataInitializer() {
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // 🎨 Initialize global theme system FIRST
        initializeTheme();
        
        if (!IS_CLOUD_DEMO) {
          console.log('🔒 Initializing secure storage...');
          initSecureStorage();
        }

        const isAvailable = await dbService.isAvailable();
        console.log(`Database access check: ${isAvailable ? 'SUCCESS' : 'FAILED'}`);

        if (isAvailable && !IS_CLOUD_DEMO) {
          console.log('Database access check: OK');
        }
      } catch (error) {
        console.error('Error during app initialization:', error);
      }
    };

    initializeApp();
  }, []);

  return null;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

export function AppContent() {
  const { t } = useTranslation();
  const [setupComplete, setSetupComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [licenseValid, setLicenseValid] = useState(false);
  const { isAuthenticated } = useSetup();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (isQawaemDirectAccess()) {
          console.log('🚀 Qawaem direct access — skipping login and license gate');
          ensureQawaemDirectAccessSession();
          setLicenseValid(true);
          setSetupComplete(true);
          setLoading(false);
          return;
        }

        if (IS_CLOUD_DEMO) {
          console.log('🌐 Cloud demo: local license (without server auth)');
          const licenseResult = await licensingService.verifyStartupLicense();
          setLicenseValid(licenseResult.valid);
          setSetupComplete(true);
          setLoading(false);
          return;
        }

        console.log('🔍 Starting authentication and license check...');
        console.log('🔑 Checking license validity...');
        const licenseResult = await licensingService.verifyStartupLicense();

        if (!licenseResult.valid) {
          console.log('❌ License check failed:', licenseResult.reason);
          localStorage.removeItem('qawaem_user');
          setLicenseValid(false);
          setSetupComplete(false);
          return;
        }

        console.log('✅ License verified successfully:', licenseResult.reason);
        setLicenseValid(true);

        if (isAuthenticated) {
          setSetupComplete(true);
          return;
        }

        const storedUser = localStorage.getItem('qawaem_user');
        if (storedUser) {
          setSetupComplete(true);
          return;
        }

        const user = await authService.getCurrentUser();
        setSetupComplete(!!user);
      } catch (err) {
        console.error('Auth/License check error:', err);
        setSetupComplete(false);
        setLicenseValid(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [isAuthenticated]);

  if (loading) {
    if (isSaudiBrand()) {
      return (
        <div className="qawaem-loading-screen">
          <div className="qawaem-loading-card">
            <div className="qawaem-loading-spinner" aria-hidden />
            <p className="text-console text-lg font-medium">{t('common.app_init_loading')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-750 to-slate-900 flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-lg p-8 rounded-xl shadow-lg flex flex-col items-center">
          <div className="w-16 h-16 border-4 border-t-blue-500 border-blue-500/20 rounded-full animate-spin mb-4" />
          <p className="text-white text-lg">{t('common.app_init_loading')}</p>
        </div>
      </div>
    );
  }

  if (!licenseValid) {
    console.log('🚨 KILL SWITCH ACTIVATED - License invalid, redirecting to setup');
    return <Navigate to="/setup" replace />;
  }

  if (!setupComplete) {
    return <Navigate to="/setup" replace />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <div
        style={
          IS_CLOUD_DEMO
            ? {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
              }
            : { display: 'contents' }
        }
      >
      <ErrorBoundary>
        <LanguageProvider>
          <SettingsProvider>
            <ThemeProvider>
              {/* ✅ DashboardThemeProvider DELETED - Using global theme system now */}
              <ClientsProvider>
                <CategoriesProvider>
                  <ProductsProvider>
                    <DocumentsProvider>
                      <DocumentTypesProvider>
                        <PaymentsProvider>
                          <EmployeesProvider>
                            <TimeTrackingProvider>
                              <PermissionsProvider>
                                <AppDataProvider>
                                  <TransportersProvider>
                                    <ReturnsProvider>
                                      <AIAgentProvider>
                                      <LanguageHandler />
                                      <DataInitializer />
                                      <Layout>
                                        <FeatureAwareRouter />
                                      </Layout>
                                      <Toaster
                                        position="top-center"
                                        containerStyle={{ zIndex: 2147483647 }}
                                        toastOptions={{
                                          duration: 4500,
                                          className: isSaudiBrand() ? 'glass-toast' : undefined,
                                          style: isSaudiBrand()
                                            ? { zIndex: 2147483647, color: '#2e452e' }
                                            : { zIndex: 2147483647 },
                                        }}
                                      />
                                      {!isSaudiBrand() ? <AIAgentPanel /> : null}
                                      <UpdateNotificationBar />
                                      </AIAgentProvider>
                                    </ReturnsProvider>
                                  </TransportersProvider>
                                </AppDataProvider>
                              </PermissionsProvider>
                            </TimeTrackingProvider>
                          </EmployeesProvider>
                        </PaymentsProvider>
                      </DocumentTypesProvider>
                    </DocumentsProvider>
                  </ProductsProvider>
                </CategoriesProvider>
              </ClientsProvider>
            </ThemeProvider>
          </SettingsProvider>
        </LanguageProvider>
      </ErrorBoundary>
      </div>
    </QueryClientProvider>
  );
}
